<template>
  <Sheet
    :visible="visible"
    :title="$t('Campaign Notes')"
    :width="700"
    @close="visible = false"
    padded
  >
    <div id="NotesModal">
      <Card>
        <CardSection>
          <TextField v-model="newNote" :disabled="saving" multiline>
            <template #right>
              <Button 
                type="primary" 
                @click="saveNewNote" 
                :disabled="saving || !newNote || (newNote && newNote.charAt(0) === '{') || (newNote && newNote.charAt(0) === '[')"
              >{{$t('Add Note')}}</Button>
            </template>
          </TextField>
          <!-- <Checkbox :label="$t('CampaignNotes.groupChanges')" :checked="groupChanges" @change="flipGroupChanges"/> -->
        </CardSection>
      </Card>
      <Card>
        <Filters :query.sync="filter"/>
        <Tabs
          :tabs="tabs"
          v-model="selected"
        />
        <div class="Modal-notes">
          <div v-if="loading" class="Modal-notes-none">{{$t('Loading')}} ...</div>
          <div v-if="!loading && filteredNotes.length === 0" class="Modal-notes-none">{{$t('Nothing to see here')}} ...</div>
          <template v-if="filteredNotes.length">
            <div
              v-for="note in filteredNotes"
              :key="note.id"
              class="note"
            >
              <div class="head">
                <span class="type">
                  <!-- <Icon :icon="getNoteTypeIcon(note)"/> -->
                  <div v-if="groupChanges && note.objectType !== 'NOTES'" class="note-checkbox">
                    <Checkbox :checked="selectedNotes[note.id]" @change="flipNoteCheckbox(note.id)"/>
                  </div>
                  <router-link v-if="getObjectLink(note) && note.objectType === 'INCENTIVE'" :to="getObjectLink(note)" tag="a" target="_blank">#{{ note.objectId }} — {{ getNoteTypeName(note) }}</router-link>
                  <router-link v-else-if="getObjectLink(note)" :to="getObjectLink(note)" tag="a" target="_blank">{{ getNoteTypeName(note) }}</router-link>
                  <span v-else>{{ getNoteTypeName(note) }}</span>
                  <div><Caption>{{ getNoteType(note) }}</Caption></div>
                </span>
                <div class="note-info">
                  <Formatter class="note-date" type="dateTimeShort" :value="note.createdOn" />
                  <div class="note-author">{{ note.createdByName }}</div>
                </div>
                <span v-if="shouldShowDelete(note)" class="delete">
                  <Icon @click="deleteNote(note)" icon="fas fa-times"/>
                </span>
              </div>
              <div class="details">
                <template v-if="note.notes.charAt(0) !== '['">
                  <template v-if="note.notes.charAt(0) !== '{'">
                    {{parseSimpleNote(note.objectType, note.notes)}}
                  </template>
                  <template v-else>
                    {{parseSimpleNote(note.objectType, JSON.parse(note.notes).note)}}
                    <pre>{{JSON.parse(note.notes).noteIds}}</pre>
                  </template>
                </template>
                <template v-else>
                  <div v-if="parseNotes(note.notes)['RULE']" class="note-group-section">
                    <b>{{$t('CampaignNotes.rulesUpdated')}}</b>
                    <div v-for="(noteInner, index) in parseNotes(note.notes)['RULE']" :key="index" class="rule">
                      <div v-if="getIsBothJson(noteInner)" class="change-rule">
                        <div class="rule-text">
                          <rule-summary :rule="getOldValueJson(noteInner)" state="removed"/>
                          <rule-summary :rule="getNewValueJson(noteInner)"/>
                        </div>
                      </div>
                      <div v-else-if="getNewValueJson(noteInner) && !getOldValueJson(noteInner)" class="new-rule">
                        <div class="rule-text"> 
                          <rule-summary :rule="getNewValueJson(noteInner)"/>
                        </div>
                      </div>
                      <div v-else-if="!getNewValueJson(noteInner) && getOldValueJson(noteInner)" class="delete-rule">
                        <div class="rule-text">
                          <rule-summary :rule="getOldValueJson(noteInner)" state="removed"/>
                        </div>
                      </div>

                    </div>
                  </div>
                  <div v-if="parseNotes(note.notes)['OFFER']" class="note-group-section">
                    <b>{{$t('CampaignNotes.offersUpdated')}}</b>
                    <List tight class="offer-list">
                      <ListItem v-for="(noteInner, index) in parseNotes(note.notes)['OFFER']" :key="index">
                        <template v-if="!noteInner.newValue && !noteInner.oldValue">
                          {{parseAttributeForTrans(noteInner.attribute)}}
                        </template>
                        <div v-else-if="noteInner.newValue">
                          {{$t('CampaignNotes.Offer')}} <PageLink :href="getOfferUrl(JSON.parse(noteInner.newValue).id)" external>{{formatOffer(JSON.parse(noteInner.newValue)).label}}</PageLink> {{$t('CampaignNotes.was added to the segment')}}
                          <Caption>{{formatOffer(JSON.parse(noteInner.newValue)).description}}</Caption>
                        </div>
                        <div v-else-if="noteInner.oldValue">
                          {{$t('CampaignNotes.Offer')}} <PageLink :href="getOfferUrl(JSON.parse(noteInner.newValue).id)" external>{{formatOffer(JSON.parse(noteInner.oldValue)).label}}</PageLink> {{$t('CampaignNotes.was removed from the segment')}}
                          <Caption>{{formatOffer(JSON.parse(noteInner.oldValue)).description}}</Caption>
                        </div>
                      </ListItem>
                    </List>
                  </div>
                  <div v-if="parseNotes(note.notes)['OTHER']" class="note-group-section">
                    <!-- <b>{{$t('Other')}}</b> -->
                    <div v-for="(noteInner, index) in parseNotes(note.notes)['OTHER']" :key="index">
                      <div v-if="note.objectType === 'ABTESTING'">
                        <div v-html="$t('CampaignNotes.fromTo', {
                          attr: $t('A/B Testing Percentage'),
                          old: noteInner.oldValue ? `${noteInner.oldValue}%` : $t('Off'),
                          new: noteInner.newValue ? `${noteInner.newValue}%` : $t('Off'),
                        })"/>
                      </div>
                      <div v-else-if="noteInner.attribute === 'RULESET' && note.objectType === 'SEGMENT'">
                        <!-- <b>{{$t('Ruleset') }} {{getRulesetAction(noteInner)}}</b>
                        <table class="table table-bordered change-table">
                          <tr>
                            <td></td>
                            <td><b>{{$t('Type')}}</b></td>
                            <td><b>{{$t('Operator')}}</b></td>
                            <td><b>{{$t('valueX', { value: 1 })}}</b></td>
                            <td><b>{{$t('valueX', { value: 2 })}}</b></td> 
                            <td><b>{{$t('valueX', { value: 3 })}}</b></td>
                          </tr>
                          <tr v-for="(rule, i) of JSON.parse(noteInner.newValue)" :key="i">
                            <td style="width:5%; padding: 0px;">
                              <i style="margin: 7px 0px 0px 7px;" :class="classFromRuleAction(rule.action)"/>
                            </td>
                            <td>{{rule.type}}</td>
                            <td>{{rule.operator}}</td>
                            <td>{{niceRuleValue(rule.value1)}}</td>
                            <td>{{niceRuleValue(rule.value2)}}</td>
                            <td>{{niceRuleValue(rule.value3)}}</td>
                          </tr>
                        </table> -->
                      </div>
                      <div v-else>
                        <template v-if="getDoAsJson(noteInner) && typeof getOldValueJson(noteInner) === 'object' && typeof getNewValueJson(noteInner) === 'object'">
                          <b>{{parseAttributeForTrans(noteInner.attribute)}}</b>
                          <table style="margin-top: 2px;" class="table table-bordered change-table">
                            <tr>
                              <td></td>
                              <td><b>{{$t('Old Value')}}</b></td>
                              <td><b>{{$t('New Value')}}</b></td>
                            </tr>
                            <template v-for="(key, i) of Object.keys(getOldValueJson(noteInner))">
                              <tr v-if="!isEqualOldNew(getOldValueJson(noteInner)[key], getNewValueJson(noteInner)[key])" :key="i">
                                <td>{{niceKey(key)}}</td>
                                <td>{{getOldValueJson(noteInner)[key]}}</td>
                                <td>{{getNewValueJson(noteInner)[key]}}</td>
                              </tr>
                            </template>
                          </table>
                        </template>
                        <template v-else-if="getDoAsJson(noteInner)">
                          <b>{{parseAttributeForTrans(noteInner.attribute)}}</b>
                          <table style="margin-top: 2px;" class="table table-bordered change-table">
                            <template v-for="(key, i) of Object.keys(getNewValueJson(noteInner))">
                              <tr v-if="getNewValueJson(noteInner)[key] !== null && getNewValueJson(noteInner)[key] !== undefined" :key="i">
                                <td>{{niceKey(key)}}</td>
                                <td>{{getNewValueJson(noteInner)[key]}}</td>
                              </tr>
                            </template>
                          </table>
                        </template>
                        <template v-else-if="noteInner.newValue !== undefined">
                          <div v-html="$t('CampaignNotes.fromTo', {
                            attr: noteInner.attribute,
                            old: fixValue(noteInner.oldValue),
                            new: fixValue(noteInner.newValue),
                          })"/>
                        </template>
                        <template v-else-if="noteInner.newValue === undefined && noteInner.oldValue === undefined">
                          {{parseAttributeForTrans(noteInner.attribute)}}
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
              <!-- <pre style="width: 100%;
                max-width: 590px;
                overflow-x: scroll;">{{note.notes}}</pre> -->
            </div>
          </template>
        </div>
        <div v-if="!loading && notes.length && notes.length === ELEMENTS_SHOWN" class="footer">
          <Button v-if="!loadingMore" :fullWidth="true" @click="loadMoreNotes">{{$t('CampaignNotes.Load More')}}</Button>
          <SkeletonBodyText v-else/>
        </div>
      </Card>
    </div>

  </Sheet>
</template>

<script>
import OffersUtil from '../../classes/utils/OffersUtil'
const ELEMENTS_SHOWN = 100
export default {
  name: 'CampaignNotesSheet',
  data() {
    return {
      visible: false,
      notes: [],
      loading: true,
      loadingMore: false,
      selected: 'ALL',
      filter: '',
      newNote: '',
      campaignId: null,
      // options: {},
      saving: false,
      // initialsColor: {},
      selectedNotes: {},
      offset: 0,
      sessionKey: null,
      accountRulesets: null,
      groupChanges: false,
      limit: ELEMENTS_SHOWN
    }
  },
  created() {
    this.eventHub.$on('showCampaignNotes', (campaignId, type = 'ALL') => {
      this.visible = true
      this.offset = 0
      this.limit = ELEMENTS_SHOWN
      this.filter = ''
      this.groupChanges = false
      this.selectedNotes = {}
      this.selected = type
      this.campaignId = campaignId
      this.$watch('selected', this.reloadNotes)
      NProgress.start()
      Promise.all([
        this.api.getCampaignNotes(this.campaignId, this.offset, this.limit, this.selected),
        this.api.getAccountRulesetsForSegmentation(),
        this.api.getSessionKey()
      ])
        .then((data) => { 
          const [notes, accountRulesets, sessionKey] = data
          this.notes = notes.data || notes
          this.accountRulesets = accountRulesets
          this.sessionKey = sessionKey

          window.RulesetEditorLoader
            .load()
            .then(lib => {
              this.$options.components.RuleSummary = lib.createRuleSummaryComponent({
                locale: this.theUser.defaultLanguage,
                authKey: this.sessionKey,
                campaignId: this.campaignId,
                accountRulesets: this.accountRulesets
              })
              this.loading = false 
            })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    })
  },
  beforeDestroy() {
    this.eventHub.$off('showCampaignNotes')
  },
  computed: {
    tabs() {
      return [
        { id: 'ALL', label: this.$t('All') },
        { id: 'NOTES', label: this.$t('Notes') },
        { id: 'CAMPAIGN', label: this.$t('Campaign') },
        { id: 'INCENTIVE', label: this.$t('Offer') },
        { id: 'SEGMENT', label: this.$t('Segment') },
        { id: 'DOMAIN', label: this.$t('Domain') },
        { id: 'ABTESTING', label: this.$t('A/B Testing') }
      ]
    },
    filteredNotes() {
      let notes = this.notes
      if (this.selected) {
        if (this.selected === 'ALL') {
          notes = this.notes.filter(note => note.objectType !== 'ABTESTING')
        } else {
          notes = this.notes.filter(note => note.objectType === this.selected)
        }
      }
      if (this.filter) {
        const filter = this.filter.toLowerCase()
        notes = this.notes.filter((note) => {
          if (note.createdByName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          if (note.notes.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          if (note.objectName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          return false
        })
      }
      return notes
    }
  },
  methods: {
    reloadNotes() {
      this.loading = true
      this.notes = []
      this.api.getCampaignNotes(this.campaignId, this.offset, this.limit, this.selected)
        .then((response) => {
          this.notes = response.data || response
          this.loading = false
        })
        .catch(this.apiCatch)
    },
    loadMoreNotes() {
      this.offset += ELEMENTS_SHOWN
      this.loadingMore = true
      this.api.getCampaignNotes(this.campaignId, this.offset, this.limit, this.selected)
        .then((response) => { 
          this.notes.push(...(response.data || response))
          this.loadingMore = false
        })
        .catch(this.apiCatch)
    },
    getNoteTypeIcon(note) {
      if (note.objectType === 'NOTES') {
        return 'far fa-edit'
      }
      if (note.objectType === 'INCENTIVE') {
        return 'fas fa-tag'
      }
      if (note.objectType === 'CAMPAIGN') {
        return 'fas fa-star'
      }
      if (note.objectType === 'SEGMENT' || note.objectType === 'SHAREDRULESET') {
        return 'fas fa-list-ul'
      }
      if (note.objectType === 'DOMAIN') {
        return 'fas fa-link'
      }
      if (note.objectType === 'ABTESTING') {
        return 'fas fa-percentage'
      }
      return 'fas fa-question'
    },
    getObjectLink(note) {
      if (note.objectType === 'INCENTIVE') {
        return `/campaigns/${this.campaignId}/offers/edit/${note.objectId}`
      }
      if (note.objectType === 'CAMPAIGN') {
        return `/campaigns/${this.campaignId}/details/`
      }
      if (note.objectType === 'SEGMENT') {
        return `/campaigns/${this.campaignId}/segmentation/segment/${note.objectId}/`
      }
      if (note.objectType === 'DOMAIN') {
        return `/campaigns/${this.campaignId}/domains/`
      }
      if (note.objectType === 'ABTESTING') {
        return `/campaigns/${this.campaignId}/options`
      }
      return ''
    },
    getNoteTypeName(note) {
      if (note.objectType === 'NOTES') {
        return this.$t('General')
      }
      if (note.objectType === 'INCENTIVE') {
        return note.objectName || this.$t('Incentive')
      }
      if (note.objectType === 'CAMPAIGN') {
        return note.objectName || this.$t('Campaign')
      }
      if (note.objectType === 'SEGMENT') {
        return note.objectName || this.$t('Segment')
      }
      if (note.objectType === 'DOMAIN') {
        return this.$t('Domain')
      }
      if (note.objectType === 'SHAREDRULESET') {
        return this.$t('Shared Ruleset')
      }
      if (note.objectType === 'ABTESTING') {
        return note.objectName || this.$t('A/B Testing')
      }
      return this.$t('Unknown')
    },
    flipNoteCheckbox(noteId) {
      if (!this.selectedNotes[noteId]) {
        this.selectedNotes[noteId] = true
      } else {
        this.selectedNotes[noteId] = false
      }
      console.log(this.selectedNotes)
    },
    flipGroupChanges() {
      if (this.groupChanges) {
        this.selectedNotes = {}
        this.groupChanges = false
      } else {
        this.groupChanges = true
      }
    },
    getNoteType(note) {
      const t = this.tabs.find(t => t.id === note.objectType)
      return t ? t.label : '?'
    },
    shouldShowDelete(note) {
      if (this.theUser && note.objectType === 'NOTES') {
        const currDate = window.moment.tz(window.moment().utc(), 'UTC').tz(this.theUser.accountTimezone)
        if (currDate.diff(window.moment.tz(note.createdOn, this.theUser.accountTimezone), 'minutes') < 10) {
          return true
        }
      }
      return false
    },
    saveNewNote() {
      if (this.newNote.trim().length === 0) {
        return
      }
      NProgress.start()
      this.saving = true
      const selectedNotesArray = Object.keys(this.selectedNotes)
      this.api.addCampaignNote(this.campaignId, this.newNote, this.groupChanges ? selectedNotesArray : null)
        .then((newNoteId) => {
          let formattedNewNote = this.newNote
          if (this.groupChanges) {
            formattedNewNote = JSON.stringify({
              note: this.newNote,
              noteIds: selectedNotesArray
            })
          }
          // add to list
          this.notes.unshift({
            id: newNoteId,
            campaignId: this.campaignId,
            notes: formattedNewNote,
            objectType: 'NOTES',
            objectId: this.campaignId,
            userId: -1,
            accountId: -1,
            onBehalfOfAccountId: null,
            createdBy: -1,
            createdOn: window.moment.tz(window.moment.utc(), 'UTC').tz(this.theUser.accountTimezone).format('YYYY-MM-DD HH:mm:ss'),
            createdByName: this.$t('Me')
          })

          this.newNote = ''
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },
    deleteNote(note) {
      NProgress.start()
      this.api.removeCampaignNote(this.campaignId, note)
        .then(() => {
          let noteIndex = -1
          this.notes.forEach((n, index) => {
            if (n.id === note.id) {
              noteIndex = index
            }
          })
          if (noteIndex > -1) {
            this.notes.splice(noteIndex, 1)
          } else {
            this.$ui.alert(this.$t('CampaignNotes.Could not find note to remove'))
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    isEqualOldNew(oldValue, newValue) {
      if (oldValue === null || oldValue === undefined) oldValue = ''
      if (newValue === null || newValue === undefined) newValue = ''
      return JSON.stringify(oldValue) === JSON.stringify(newValue)
    },
    getRulesetAction(noteInner) {
      if (noteInner.action === 'ADD') {
        return this.$t('Added')
      }
      if (noteInner.action === 'DELETE') {
        return this.$t('Deleted')
      }
      if (noteInner.action === 'UPDATE') {
        return this.$t('Updated')
      }
    },
    getIsBothJson(noteInner) {
      let isBothJson = true
      try {
        JSON.parse(noteInner.oldValue)
      } catch(e) {
        isBothJson = false
      }
      try {
        JSON.parse(noteInner.newValue)
      } catch(e) {
        isBothJson = false
      }
      return isBothJson
    },
    getOldValueJson(noteInner) {
      let oldValueJson
      try {
        oldValueJson = JSON.parse(noteInner.oldValue)
      } catch (e) {
        return ''
      }
      return oldValueJson
    },
    getNewValueJson(noteInner) {
      let newValueJson
      try {
        newValueJson = JSON.parse(noteInner.newValue)
      } catch (e) {
        return ''
      }
      return newValueJson
    },
    getDoAsJson(noteInner) {
      let doAsJson = false
      const oldValueJson = this.getOldValueJson(noteInner)
      const newValueJson = this.getNewValueJson(noteInner)
      if (oldValueJson && newValueJson && (typeof oldValueJson === 'object')) {
        doAsJson = true
      }
      return doAsJson
    },
    formatOffer(offer) {
      return OffersUtil.formatOffer(offer)
    },
    getOfferUrl(id) {
      return `/#/campaigns/${this.campaignId}/offers/edit/${id}`
    },
    parseNotes(strNotes) {
      const notes = JSON.parse(strNotes)
      const rv = {}
      notes.forEach((noteInner) => {
        if (noteInner.attribute === 'RULE') {
          if (!rv['RULE']) {
            rv['RULE'] = []
          }
          rv['RULE'].push(noteInner)
        } else if (noteInner.attribute.toLowerCase().substring(0, 5) === 'offer') {
          if (!rv['OFFER']) {
            rv['OFFER'] = []
          }
          rv['OFFER'].push(noteInner)
        } else {
          if (!rv['OTHER']) {
            rv['OTHER'] = []
          }
          rv['OTHER'].push(noteInner)
        }
      })
      return rv
    },
    niceKey(val) {
      return val.charAt(0).toUpperCase() + val.substr(1).replace(/([A-Z])/g, ' $1')
    },
    niceRuleValue(val) {
      if (val === true) {
        return this.$t('TRUE')
      }
      if (val === false) {
        return this.$t('FALSE')
      }
      if (val === 0) {
        return '0'
      }
      if (!val) {
        return ''
      }
      return val
    },
    fixValue(value) {
      if (value === null || value === undefined || value === '') {
        return '""'
      }
      if (typeof(value) === 'string' && value === 'Default') {
        return this.$t('Default')
      }
      if (typeof(value) === 'object') {
        return JSON.stringify(value)
      }
      if (typeof(value) === 'string' && value.match(/19[0-9][0-9]-[0-9][0-9]-[0-9][0-9]/) && parseInt(value.substr(0, 4), 10) < 1980) {
        return '""'
      }
      if (typeof(value) === 'string' && value.indexOf('cloudfront.net') > -1) {
        return `<div style="background: url(${value});
          width: 30px;
          height: 30px;
          background-size: contain;
          display: inline-block;
          background-repeat: no-repeat;
          background-position: center bottom;
        "></div>`
      }
      return value
    },
    classFromRuleAction(ruleAction) {
      let cssClass = ''
      switch (ruleAction) {
        case 'ADD':
          cssClass = 'fas fa-plus-circle'
          break
        case 'UPDATE':
          cssClass = 'fas fa-pen-square'
          break
        case 'DELETE':
          cssClass = 'fas fa-times-circle red'
          break
        default:
      }
      return cssClass
    },
    // mega hack for translations
    parseSimpleNote(objectType, note){
      let returnNoteStr = null
      try {
        returnNoteStr = decodeURIComponent(note.replace(/\\n/g, '<br />'))
      } catch (e) {
        returnNoteStr = note.replace(/\\n/g, '<br />')
      }
      if (objectType === 'INCENTIVE') {
        returnNoteStr = returnNoteStr.replace(/^Added incentive/, this.$t('CampaignNotes.Added incentive'))
        returnNoteStr = returnNoteStr.replace(/^Deleted incentive/, this.$t('CampaignNotes.Deleted incentive'))
      } else if (objectType === 'CAMPAIGN') {
        returnNoteStr = returnNoteStr.replace(/^Added campaign/, this.$t('CampaignNotes.Added campaign'))
      } else if (objectType === 'SEGMENT') {
        returnNoteStr = returnNoteStr.replace(/^Added segment/, this.$t('CampaignNotes.Added segment'))
        returnNoteStr = returnNoteStr.replace(/^Deleted segment/, this.$t('CampaignNotes.Deleted segment'))
      } else if (objectType === 'SHAREDRULESET') {
        returnNoteStr = returnNoteStr.replace(/^Added shared ruleset/, this.$t('CampaignNotes.Added shared ruleset'))
        returnNoteStr = returnNoteStr.replace(/^Deleted shared ruleset/, this.$t('CampaignNotes.Deleted shared ruleset'))
      } else if (objectType === 'DOMAIN') {
        returnNoteStr = returnNoteStr.replace(/^Linked/, this.$t('CampaignNotes.Linked'))
        returnNoteStr = returnNoteStr.replace(/to campaign$/, this.$t('CampaignNotes.to campaign'))
        returnNoteStr = returnNoteStr.replace(/^Unlinked/, this.$t('CampaignNotes.Unlinked'))
        returnNoteStr = returnNoteStr.replace(/from campaign$/, this.$t('CampaignNotes.from campaign'))
      }
      return returnNoteStr
    },
    parseAttributeForTrans(attribute) {
      if (attribute.includes('Offer ') && attribute.includes(' was added to the segment')) {
        attribute = attribute.replace(/^Offer/, this.$t('CampaignNotes.Offer'))
        attribute = attribute.replace(/was added to the segment$/, this.$t('CampaignNotes.was added to the segment'))
      } else if (attribute.includes('Offer ') && attribute.includes(' was removed from the segment')) {
        attribute = attribute.replace(/^Offer/, this.$t('CampaignNotes.Offer'))
        attribute = attribute.replace(/was removed from the segment$/, this.$t('CampaignNotes.was removed from the segment'))
      } else {
        attribute = this.$t(`${attribute}`)
      }
      return attribute
    }
  },
  components: {
    // RuleSummary: RulesetEditor.createRuleSummaryComponent(ruleSummaryProps)
  }
}
</script>

<style lang="scss">
#NotesModal {
  font-size: 14px;
  .Modal-notes {
    // height: 50vh;
    overflow: hidden;
    // border: 1px solid #ccc;
    // overflow-y: scroll;
    .delete {
      margin-left: 15px;
      margin-top: -2px;
      cursor: pointer;
      color: red;
    }
    .note {
      hr {
        width: 80%;
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .head {
        display: flex;
        align-items: center;
        background: #f5f5f5;
        padding: 10px;
        border-top: 1px solid #ddd;
        .type {
          flex: 1;
          .note-checkbox {
            float: left;
          }
          i {
            color: #fff;
            background: #9999;
            font-size: 15px;
            width: 25px;
            height: 25px;
            border-radius: 50%;
            display: inline-block;
            padding: 5px;
            padding-left: 4px;
            margin-right: 5px;
          }
        }
        .note-info {
          color: #666;
          font-size: 80%;
          text-align: right;
        }
      }
      .details {
        margin: 15px;
        font-size: 110%;
        .note-group-section:first-of-type {
          margin-top: 0px;
        }
        .note-group-section {
          margin-top: 8px;
        }
        .rule:first-of-type {
          margin-top: 5px;
        }
        .rule {
          .delete-rule {
            margin-bottom: 1px;
            background: #fef0ef;
            border-left: 4px solid #f01800;
          }
          .new-rule {
            margin-bottom: 1px;
            background: #e9f3e9;
            border-left: 4px solid green;
          }
          .change-rule {
            margin-bottom: 1px;
            background: #edf6ff;
            border-left: 4px solid #026ebb;
          }
          .rule-text {
            font-size: 14px;
            padding: 8px 16px 8px 12px;
          }
        }
        .offer-list {
          margin-top: 5px;
        }
      }
    }
  }

  .footer {
    padding: 10px 15px;
    border-top: 1px solid #ccc;
  }
  .red {
    color: red;
  }

  .Modal-notes-none {
    text-align: center;
    margin-top: 30px;
    margin-bottom: 30px;
    font-size: 120%;
    color: #666;
  }
  table.change-table {
    margin-top: 5px;
    // margin-left: -9px;
    font-size: 90%;
    border-radius: 0px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    td {
      padding: 3px;
      vertical-align: top;
      line-height: 22px;
      text-align: left;
    }
  }
}
</style>
